import { AlertTriangle, Info } from "lucide-react";
import { useEffect, useId, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "../../i18n";
import Button from "../inputs/Button";
import InputForm from "../inputs/InputForm";

export type ConfirmationVariant = "default" | "danger";

export interface ConfirmationProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmationVariant;
  reason?: {
    label: string;
    placeholder?: string;
    requiredMessage?: string;
    defaultValue?: string;
  };
  quantity?: {
    label: string;
    requiredMessage?: string;
    defaultValue?: number;
    min?: number;
  };
  /** Called when the user confirms the action. */
  onConfirm: (reason?: string, quantity?: number) => void;
  /** Called when the user cancels, presses Escape, or clicks the overlay. */
  onCancel: () => void;
}

/**
 * Themed confirmation dialog for destructive or irreversible choices.
 * Reuse this instead of `window.confirm` so copy, RTL, and branding stay consistent.
 */
export default function Confirmation({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = "default",
  reason,
  quantity,
  onConfirm,
  onCancel,
}: ConfirmationProps) {
  const { t, dir } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();
  const confirmRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors },
  } = useForm<{ reason: string; quantity: number | "" }>({
    defaultValues: {
      reason: reason?.defaultValue ?? "",
      quantity: quantity?.defaultValue ?? "",
    },
  });

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() =>
      reason ? setFocus("reason") : confirmRef.current?.focus(),
    );
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onCancel, reason, setFocus]);

  useEffect(() => {
    if (open) {
      reset({
        reason: reason?.defaultValue ?? "",
        quantity: quantity?.defaultValue ?? "",
      });
    }
  }, [open, quantity?.defaultValue, reason?.defaultValue, reset]);

  if (!open) return null;

  const resolvedConfirm = confirmLabel ?? t("common.confirm");
  const resolvedCancel = cancelLabel ?? t("common.cancel");
  const Icon = variant === "danger" ? AlertTriangle : Info;

  return (
    <div
      ref={overlayRef}
      dir={dir}
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      onClick={(event) => {
        if (event.target === overlayRef.current) onCancel();
      }}
    >
      <div
        className="absolute inset-0 bg-overlay backdrop-blur-sm"
        aria-hidden
      />
      <form
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="relative w-full max-w-md overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-2xl"
        style={{ animation: "confirmationIn 0.18s ease-out" }}
        onSubmit={handleSubmit((values) =>
          onConfirm(
            reason ? values.reason.trim() : undefined,
            quantity && values.quantity !== ""
              ? Number(values.quantity)
              : undefined,
          ),
        )}
      >
        <div className="flex items-start gap-4 px-6 pt-6">
          <div
            className={
              variant === "danger"
                ? "flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive"
                : "flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary"
            }
          >
            <Icon className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h2
              id={titleId}
              className="text-lg font-semibold text-card-foreground"
            >
              {title}
            </h2>
            <p
              id={descriptionId}
              className="mt-2 text-sm leading-relaxed text-muted-foreground"
            >
              {message}
            </p>
          </div>
        </div>

        {reason && (
          <div className="px-6 pt-5">
            <InputForm
              label={reason.label}
              required
              placeholder={reason.placeholder}
              field={register("reason", {
                required: reason.requiredMessage || reason.label,
                validate: (value) =>
                  value.trim().length > 0 ||
                  reason.requiredMessage ||
                  reason.label,
              })}
              errors={{ message: errors.reason?.message }}
            />
          </div>
        )}
        {quantity && (
          <div className="px-6 pt-5">
            <InputForm
              inputType="number"
              label={quantity.label}
              required
              min={quantity.min}
              field={register("quantity", {
                required: quantity.requiredMessage || quantity.label,
                valueAsNumber: true,
              })}
              errors={{ message: errors.quantity?.message }}
            />
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-border bg-muted/40 px-6 py-4">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            {resolvedCancel}
          </Button>
          <Button
            ref={confirmRef}
            type="submit"
            variant={variant === "danger" ? "danger" : "primary"}
            size="sm"
          >
            {resolvedConfirm}
          </Button>
        </div>
      </form>

      <style>{`
        @keyframes confirmationIn {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
